import React, { useEffect, useState, useRef } from 'react'

interface Props {
    value: number
    label: React.ReactNode
    className?: string
    animate?: boolean
}

export default function AnimateNumberTickUp({
    value,
    label,
    className = '',
    animate = true,
}: Props): React.JSX.Element {
    const [displayedValue, setDisplayedValue] = useState<number>(0)
    const animationRef = useRef<number | null>(null)
    const targetValueRef = useRef<number>(value)
    const isAnimatingRef = useRef<boolean>(false)

    // Animation function to smoothly count up to target
    const animateCounter = React.useCallback(() => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current)
        }

        isAnimatingRef.current = true
        const ticksPerSecond = 15 // Increased for smoother animation
        const intervalMs = 1000 / ticksPerSecond
        let lastUpdateTime = 0

        const animate = (timestamp: number) => {
            if (timestamp - lastUpdateTime >= intervalMs) {
                setDisplayedValue((current) => {
                    const currentTarget = targetValueRef.current

                    if (current >= currentTarget) {
                        isAnimatingRef.current = false
                        return currentTarget
                    }

                    // Calculate increment - faster for larger differences, but with minimum speed
                    const difference = currentTarget - current
                    const baseIncrement = Math.max(
                        1,
                        Math.ceil(difference / 15),
                    )
                    // Add a minimum increment to ensure it always moves forward
                    const increment = Math.max(
                        baseIncrement,
                        Math.ceil(difference * 0.08),
                    )
                    const nextValue = current + increment

                    // Don't overshoot
                    if (nextValue >= currentTarget) {
                        isAnimatingRef.current = false
                        return currentTarget
                    } else {
                        animationRef.current = requestAnimationFrame(animate)
                        return nextValue
                    }
                })
                lastUpdateTime = timestamp
            } else {
                animationRef.current = requestAnimationFrame(animate)
            }
        }

        animationRef.current = requestAnimationFrame(animate)
    }, [])

    // Update target and start animation when value changes
    useEffect(() => {
        const newTarget = value
        const oldTarget = targetValueRef.current

        // Update the target
        targetValueRef.current = newTarget

        if (!animate) {
            setDisplayedValue(newTarget)
            return
        }

        // Only start animation if the new target is higher and we're not already animating to a higher value
        if (newTarget > oldTarget) {
            animateCounter()
        }
    }, [value, animate, animateCounter])

    useEffect(() => {
        if (!animate) {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current)
                animationRef.current = null
            }
            isAnimatingRef.current = false
            setDisplayedValue(targetValueRef.current)
        }
    }, [animate])

    // Cleanup animation on unmount
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current)
            }
            isAnimatingRef.current = false
        }
    }, [])

    return (
        <div className={`text-center ${className}`}>
            <div className="text-sm text-gray-600 font-medium mb-1">
                {label}
            </div>
            <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {displayedValue.toLocaleString()}
            </div>
        </div>
    )
}
